import { supabase } from '@/integrations/supabase/client';

export type FunnelStep = 
  | 'page_loaded'
  | 'rating_selected' 
  | 'email_step_shown'
  | 'email_entered'
  | 'email_skipped'
  | 'redirected_to_google'
  | 'feedback_form_shown'
  | 'custom_questions_answered'
  | 'review_submitted'
  | 'process_completed';

export interface FunnelEventData {
  sessionId: string;
  businessId: string;
  stepName: FunnelStep;
  stepData?: Record<string, any>;
  tagId?: string | null;
  tagName?: string | null;
  formId?: string | null;
}

// Generate a unique session ID for tracking
export const generateSessionId = (): string => {
  return crypto.randomUUID();
};

// Track a funnel step
export const trackFunnelStep = async (eventData: FunnelEventData): Promise<void> => {
  try {
    const { error } = await supabase
      .from('review_funnel_events')
      .insert([{
        session_id: eventData.sessionId,
        business_id: eventData.businessId,
        step_name: eventData.stepName,
        step_data: eventData.stepData || {},
        tag_id: eventData.tagId,
        tag_name: eventData.tagName,
        form_id: eventData.formId,
        user_agent: navigator.userAgent
      }]);

    if (error) {
      console.error('Failed to track funnel step:', error);
    }
  } catch (error) {
    console.error('Error tracking funnel step:', error);
  }
};

// Get funnel analytics for a business
export const getFunnelAnalytics = async (businessId: string, days: number = 30) => {
  try {
    const { data, error } = await supabase.functions.invoke('funnel-analytics', {
      body: { businessId, days }
    });

    if (error) throw error;
    
    if (data?.success) {
      return data.data.conversions;
    } else {
      throw new Error(data?.error || 'Failed to fetch funnel analytics');
    }
  } catch (error) {
    console.error('Error fetching funnel analytics:', error);
    // Fallback to direct database query
    try {
      const { data, error } = await supabase
        .from('review_funnel_events')
        .select('step_name, session_id, timestamp, step_data, form_id')
        .eq('business_id', businessId)
        .gte('timestamp', new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString())
        .order('timestamp', { ascending: true });

      if (error) throw error;
      return data;
    } catch (fallbackError) {
      console.error('Fallback query also failed:', fallbackError);
      return [];
    }
  }
};

// Calculate conversion rates between steps
export const calculateFunnelConversion = (events: any[]) => {
  const stepCounts: Record<string, { total: number; unique: number }> = {};
  const sessionSteps: Record<string, Set<string>> = {};

  // Count total events and unique sessions per step
  events.forEach(event => {
    const step = event.step_name;
    const sessionId = event.session_id;

    if (!stepCounts[step]) {
      stepCounts[step] = { total: 0, unique: 0 };
    }
    if (!sessionSteps[step]) {
      sessionSteps[step] = new Set();
    }

    stepCounts[step].total++;
    sessionSteps[step].add(sessionId);
  });

  // Update unique counts
  Object.keys(sessionSteps).forEach(step => {
    stepCounts[step].unique = sessionSteps[step].size;
  });

  // Calculate conversion rates
  const stepOrder: FunnelStep[] = [
    'page_loaded',
    'rating_selected',
    'email_step_shown',
    'email_entered',
    'feedback_form_shown',
    'custom_questions_answered',
    'review_submitted',
    'process_completed'
  ];

  const conversions = stepOrder.map((step, index) => {
    const current = stepCounts[step] || { total: 0, unique: 0 };
    const previous = index > 0 ? stepCounts[stepOrder[index - 1]] : null;
    
    const conversionRate = previous && previous.unique > 0 
      ? (current.unique / previous.unique) * 100 
      : 100;

    return {
      step,
      count: current.unique,
      conversionRate: Math.round(conversionRate * 100) / 100,
      dropOff: previous ? previous.unique - current.unique : 0
    };
  });

  return conversions.filter(c => c.count > 0);
};